import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, Animated, Easing,
} from 'react-native';
import Colors from '../../../../../constants/Colors';
import { MetricPropTypes, MetricProps, MetricThreshold } from '../../../../../graphs/AirQualityGraph';
import { Metric } from '../../../../../constants/Types';

interface MetricValueCardProps {
  metric: MetricProps,
  value: number,
  critical?: boolean,
  count: number,
}

export const safetyLevelTextColor = (metric: MetricProps, value: number) => {
  let outputColor: string = styles.score.color;
  if (!(metric.thresholds?.length > 0 && Number.isFinite(value))) return outputColor;
  if (metric.table === Metric.TEMPERATURE) {
    const tempThresholds: [number, number, number] = [
      parseInt(metric.thresholds[0].value.split('-')[0], 10),
      parseInt(metric.thresholds[1].value, 10),
      parseInt(metric.thresholds[0].value.split('-')[1], 10),
    ];
    if (value === tempThresholds[1]) return metric.thresholds[0].color;
    if (value >= tempThresholds[0] && value <= tempThresholds[2]) return metric.thresholds[1].color;
    return Colors.light.hereworksOrange500;
  }
  if (metric.table === Metric.FORMALDEHYDE) {
    const formaldehydeThresholds: [number, number] = [
      parseInt(metric.thresholds[0].value, 10),
      parseInt(metric.thresholds[2].value, 10),
    ];
    if (value < formaldehydeThresholds[0]) return metric.thresholds[0].color;
    if (value >= formaldehydeThresholds[0]
      && value < formaldehydeThresholds[1]) return metric.thresholds[1].color;
    return metric.thresholds[1].color;
  }
  metric.thresholds.forEach((threshold: MetricThreshold) => {
    if (value >= parseInt(threshold.value, 10)) outputColor = threshold.color;
  });
  return outputColor;
};

const MetricValueCard = ({ metric, value, count = 6 }: MetricValueCardProps) => {
  const [scoreOpacity] = useState(new Animated.Value(1));

  useEffect(() => {
    Animated.sequence([
      Animated.timing(scoreOpacity, { toValue: 0, duration: 0, useNativeDriver: true }),
      Animated.timing(scoreOpacity, {
        toValue: 1, duration: 500, useNativeDriver: true, easing: Easing.ease,
      }),
    ]).start();
  }, [value]);

  return <View style={[styles.container, { flex: 1 / count }]}>
    <Text style={styles.title}>{metric.title}</Text>
    <View style={styles.textContainer}>
      <Animated.Text style={[
        styles.score,
        {
          color: safetyLevelTextColor(metric, value),
          opacity: scoreOpacity,
        },
      ]}>
        {metric.type === MetricPropTypes.BINARY ? `${value ? 'Yes' : 'No'}` : value ?? '-'}
      </Animated.Text>
      {/* {showComparison && <Text
        style={[
          styles.metricValueText, styles.metricValueTextCompared,
          critical && { color: Colors.light.hereworksOrange700 },
        ]}>{'-'}</Text>} */}
      <Text style={styles.unit}>{metric.unit}</Text>
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    minWidth: 120,
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 12,
    paddingTop: 10,
    paddingBottom: 8,
    paddingHorizontal: 12,
    marginHorizontal: 6,
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 12,
    lineHeight: 9.6,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack500,
    marginBottom: 8,
  },
  score: {
    fontSize: 20,
    lineHeight: 24,
    fontFamily: 'Poppins_500Medium',
    marginEnd: 2,
    color: Colors.light.hereworksEmeraldGreen,
  },
  scoreCompared: {
    marginStart: 6,
  },
  unit: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack500,
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
});

export default MetricValueCard;
